import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { IAdminUser, deactivateAdminUser } from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';

interface Props {
	/** The user to deactivate; null closes the dialog. */
	user: IAdminUser | null;
	onClose: () => void;
	/** Called once the backend has confirmed the deactivation. */
	onDeactivated: () => void;
}

/**
 * Confirmation step for deactivating one account. The admin has to type the user's email
 * before the button enables; the backend flips the flag and blocks the Auth0 login as one step.
 */
export default function DeactivateUserDialog({ user, onClose, onDeactivated }: Props) {
	const queryClient = useQueryClient();
	const [typedEmail, setTypedEmail] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const confirmed = !!user && typedEmail.trim().toLowerCase() === user.email.toLowerCase();

	function handleClose() {
		if (submitting) return;
		setTypedEmail('');
		setError(null);
		onClose();
	}

	async function handleConfirm() {
		if (!user || !confirmed) return;
		setSubmitting(true);
		setError(null);
		try {
			await deactivateAdminUser(user.id);
			// The users list hides inactive users. The detail query is left alone: the
			// user page is about to unmount, and refetching it would only 404.
			await queryClient.invalidateQueries(['admin-users'], { exact: true });
			setTypedEmail('');
			onDeactivated();
		} catch (e: any) {
			setError(apiErrorMessage(e, 'Failed to deactivate the account.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog open={!!user} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundImage: 'none' } }}>
			<DialogTitle>Deactivate Account</DialogTitle>
			<DialogContent>
				{error && (
					<Alert severity="error" sx={{ mb: 1.5 }}>
						{error}
					</Alert>
				)}
				<Typography variant="body2" sx={{ mb: 1 }}>
					<strong>{user?.full_name || user?.email}</strong> will lose access to the platform immediately and
					be blocked from logging in again. Their views, worksheets, watchlists and dataset permissions are
					kept. This cannot be undone from the dashboard.
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
					Type the user's email to confirm.
				</Typography>
				<TextField
					autoFocus
					fullWidth
					size="small"
					placeholder={user?.email}
					value={typedEmail}
					onChange={(e) => setTypedEmail(e.target.value)}
					disabled={submitting}
					inputProps={{ autoComplete: 'off', spellCheck: false }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={submitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="error"
					onClick={handleConfirm}
					disabled={!confirmed || submitting}
					startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
				>
					{submitting ? 'Deactivating…' : 'Deactivate'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
